interface MarkdownCache {
  listItems: string[];
  paragraphLines: string[];
}

export class StringTools {
  /**
   * Returns true if the given text doesn't contain content (after trimming).
   */
  public static isEmpty(text: string | undefined): boolean {
    return text === undefined || text === null || text.trim() === '';
  }

  /**
   * Converts a count to a string (max 99).
   */
  public static toCountText(count: number): string {
    if (count > 99) {
      return '99+';
    } else if (count > 0) {
      return `${count}`;
    } else {
      return '';
    }
  }

  /**
   * Converts a name to initials (maximum 2 characters).
   */
  public static toInitials(name: string): string {
    const words = name?.split(' ') || [];
    if (words.length === 0) {
      return '';
    }
    return words.length === 1
      ? words[0].charAt(0).toUpperCase()
      : [words[0], words[words.length - 1]].map(word => word.charAt(0).toUpperCase()).join('');
  }

  /**
   * Convert the given kebab case string to upper camel case.
   */
  public static kebabCaseToUpperCamelCase(str: string): string {
    const lowerCamelCase = this.kebabCaseToLowerCamelCase(str);
    return lowerCamelCase.charAt(0).toUpperCase() + lowerCamelCase.slice(1);
  }

  /**
   * Convert the given kebab case string to lower camel case.
   */
  public static kebabCaseToLowerCamelCase(str: string): string {
    return str.replace(/-([a-z])/g, g => g[1].toUpperCase());
  }

  /**
   * Convert the given markdown string to HTML.
   */
  public static markdownToHtml(raw: string): string {
    let cache: MarkdownCache = {
      listItems: [],
      paragraphLines: [],
    };
    let html = '';
    const rawEscaped = StringTools.escapeHtml(raw);
    const lines = rawEscaped.split('\n');
    const listRegex = /^\s*-\s*(.*)$/;
    for (let line of lines) {
      line = line.replace('\r', '');
      let titleFound = false;

      // Titles
      if (line.startsWith('#### ')) {
        html += StringTools.purgeMarkdownCache(cache);
        html += `<div class="sol-markdown-h4">${line.slice(5)}</div>`;
        titleFound = true;
      } else if (line.startsWith('### ')) {
        html += StringTools.purgeMarkdownCache(cache);
        html += `<div class="sol-markdown-h3">${line.slice(4)}</div>`;
        titleFound = true;
      } else if (line.startsWith('## ')) {
        html += StringTools.purgeMarkdownCache(cache);
        html += `<div class="sol-markdown-h2">${line.slice(3)}</div>`;
        titleFound = true;
      } else if (line.startsWith('# ')) {
        html += StringTools.purgeMarkdownCache(cache);
        html += `<div class="sol-markdown-h1">${line.slice(2)}</div>`;
        titleFound = true;
      }

      // Multi-line elements
      if (!titleFound) {
        // List items
        let listItemFound = false;
        const listItemMatch = line.match(listRegex);
        if (listItemMatch) {
          html += StringTools.purgeMarkdownCache(cache, false, true);
          listItemFound = true;
          cache.listItems.push(listItemMatch[1]);
        } else {
          html += StringTools.purgeMarkdownCache(cache, true, false);
        }

        // Paragraph
        if (!listItemFound) {
          if (cache.paragraphLines.length > 0 && line.trim() === '') {
            html += StringTools.purgeMarkdownCache(cache);
          }
          cache.paragraphLines.push(line);
        }
      }
    }
    html += StringTools.purgeMarkdownCache(cache);
    return html;
  }

  private static purgeMarkdownCache(cache: MarkdownCache, list: boolean = true, paragraph: boolean = true): string {
    // List items
    let html = '';
    if (list && cache.listItems.length > 0) {
      const listItems = cache.listItems;
      html += `<div class="sol-markdown-list">${listItems.map(item => `<div class="sol-markdown-list-item">${StringTools.stylizeMarkdownLine(item)}</div>`).join('')}</div>`;
      cache.listItems = [];
    }

    // Paragraphs
    if (paragraph && cache.paragraphLines.length > 0) {
      const lines = cache.paragraphLines;
      const isEmpty = lines.every(line => line.trim() === '');
      if (!isEmpty) {
        html += `<div class="sol-markdown-paragraph">${lines.map(line => StringTools.stylizeMarkdownLine(line)).join('\n')}</div>`;
      }
      cache.paragraphLines = [];
    }
    return html;
  }

  /**
   * Finds (multiple) bold (*), italics (_) and code (`) in text within the line and encloses them with span tags.
   */
  private static stylizeMarkdownLine(line: string): string {
    const typeChars = ['*', '_', '`'];
    const typeNames = ['bold', 'italics', 'code'];
    for (let i = 0; i < typeChars.length; i++) {
      const typeChar = typeChars[i];
      const typeName = typeNames[i];
      let typeFound = false;
      let typeString = '';
      let result = '';
      for (let j = 0; j < line.length; j++) {
        const char = line.charAt(j);
        if (char === typeChar) {
          typeFound = !typeFound;

          // Purge cache
          if (!typeFound && typeString.length > 0) {
            result += `<span class="sol-markdown-${typeName}">${typeString}</span>`;
            typeString = '';
          }
        } else if (typeFound) {
          typeString += char;
        } else {
          result += char;
        }
      }

      // Purge cache
      if (!typeFound && typeString.length > 0) {
        result += `<span class="sol-markdown-${typeName}">${typeString}</span>`;
      }
      line = result;
    }
    return line;
  }

  /**
   * Escape HTML special characters.
   */
  public static escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
